import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CreditCard, Plus, Check, Trash2, Loader, AlertCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STRIPE_PUBLISHABLE_KEY = 'pk_test_51SVCSo382aFGgdDTM8fb8m063DuKfpCwaW8SSpAZ3KBkKMkGeEVhUkMUeA22P7hQwe04Bxs5eqRx0NhhAjdS87Qm001YK7K9gw';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const AddCardForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    try {
      // Get setup intent from backend
      const { data: setupData } = await axios.post(`${API}/payments/setup-intent`);
      
      // Confirm card setup
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        setupData.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          }
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add payment method');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{
        padding: '16px',
        background: 'rgba(230, 215, 195, 0.05)',
        border: '1px solid rgba(165, 124, 51, 0.3)',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#E6D7C3',
                '::placeholder': {
                  color: '#A57C33',
                },
              },
              invalid: {
                color: '#ff4757',
              },
            },
          }}
        />
      </div>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          background: 'rgba(255, 71, 87, 0.1)',
          border: '1px solid rgba(255, 71, 87, 0.3)',
          borderRadius: '8px',
          color: '#ff4757',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          style={{
            flex: 1,
            padding: '12px',
            background: 'rgba(230, 215, 195, 0.05)',
            border: '1px solid rgba(165, 124, 51, 0.3)',
            borderRadius: '8px',
            color: '#A57C33',
            fontSize: '14px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          style={{
            flex: 1,
            padding: '12px',
            background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
            border: 'none',
            borderRadius: '8px',
            color: '#1a1a1a',
            fontSize: '14px',
            fontWeight: '600',
            cursor: (!stripe || loading) ? 'not-allowed' : 'pointer',
            opacity: (!stripe || loading) ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {loading && <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />}
          {loading ? 'Adding...' : 'Add Card'}
        </button>
      </div>
    </form>
  );
};

const PaymentWalletPage = () => {
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payments/methods`);
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setMessage({ type: 'error', text: 'Failed to load payment methods' });
    } finally {
      setLoading(false);
    }
  };

  const getCardIcon = (type) => {
    const icons = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳'
    };
    return icons[type] || '💳';
  };

  const getCardName = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.post(`${API}/payments/methods/${id}/default`);
      setMessage({ type: 'success', text: 'Default payment method updated' });
      await fetchPaymentMethods();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to set default payment method' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) return;
    
    try {
      await axios.delete(`${API}/payments/methods/${id}`);
      setMessage({ type: 'success', text: 'Payment method removed' });
      await fetchPaymentMethods();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove payment method' });
    }
  };

  const handleCardAdded = async () => {
    setMessage({ type: 'success', text: 'Payment method added successfully' });
    setShowAddCard(false);
    await fetchPaymentMethods();
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', color: '#E6D7C3', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
        Loading payment methods...
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D7B56D', padding: '8px' }}>
          <ArrowLeft size={24} />
        </button>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>Payment & Wallet</h1>
      </div>

      <div style={{ padding: '20px', paddingBottom: '100px' }}>
        {/* Message Display */}
        {message.text && (
          <div style={{
            padding: '16px',
            marginBottom: '24px',
            borderRadius: '12px',
            background: message.type === 'success' ? 'rgba(46, 213, 115, 0.1)' : 'rgba(255, 71, 87, 0.1)',
            border: `1px solid ${message.type === 'success' ? '#2ed573' : '#ff4757'}`,
            color: message.type === 'success' ? '#2ed573' : '#ff4757',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {message.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Payment Methods */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#D7B56D', fontFamily: 'Playfair Display, serif' }}>
              Payment Methods
            </h3>
            {!showAddCard && (
              <button
                onClick={() => setShowAddCard(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.2) 100%)',
                  border: '1px solid rgba(215, 181, 109, 0.5)',
                  borderRadius: '8px',
                  color: '#D7B56D',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <Plus size={16} />
                Add Card
              </button>
            )}
          </div>

          {/* Add Card Form */}
          {showAddCard && (
            <div style={{ marginBottom: '24px' }}>
              <Elements stripe={stripePromise}>
                <AddCardForm 
                  onSuccess={handleCardAdded}
                  onCancel={() => setShowAddCard(false)}
                />
              </Elements>
            </div>
          )}

          {paymentMethods.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <CreditCard size={48} style={{ color: '#A57C33', margin: '0 auto 16px' }} />
              <p style={{ color: '#A57C33', fontSize: '14px' }}>No payment methods added yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {paymentMethods.map(method => (
                <div
                  key={method.id}
                  style={{
                    padding: '16px',
                    background: method.isDefault 
                      ? 'linear-gradient(135deg, rgba(215, 181, 109, 0.15) 0%, rgba(165, 124, 51, 0.1) 100%)'
                      : 'rgba(230, 215, 195, 0.05)',
                    border: `1px solid ${method.isDefault ? 'rgba(215, 181, 109, 0.4)' : 'rgba(165, 124, 51, 0.3)'}`,
                    borderRadius: '12px',
                    position: 'relative'
                  }}
                >
                  {method.isDefault && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: '#2ed573',
                      color: '#fff',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Check size={12} />
                      DEFAULT
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '32px' }}>{getCardIcon(method.type)}</div>
                    <div>
                      <div style={{ color: '#E6D7C3', fontSize: '16px', fontWeight: '600' }}>
                        {getCardName(method.type)} •••• {method.last4}
                      </div>
                      <div style={{ color: '#A57C33', fontSize: '13px' }}>
                        Expires {String(method.exp_month).padStart(2, '0')}/{method.exp_year}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    {!method.isDefault && (
                      <button
                        onClick={() => handleSetDefault(method.id)}
                        style={{
                          flex: 1,
                          padding: '8px',
                          background: 'rgba(215, 181, 109, 0.1)',
                          border: '1px solid rgba(215, 181, 109, 0.3)',
                          borderRadius: '8px',
                          color: '#D7B56D',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Set as Default
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(method.id)}
                      style={{
                        padding: '8px 12px',
                        background: 'rgba(255, 71, 87, 0.1)',
                        border: '1px solid rgba(255, 71, 87, 0.3)',
                        borderRadius: '8px',
                        color: '#ff4757',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment Info */}
        <div className="card">
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#D7B56D', marginBottom: '16px', fontFamily: 'Playfair Display, serif' }}>
            Payment Information
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', borderRadius: '8px', border: '1px solid rgba(165, 124, 51, 0.3)' }}>
              <span style={{ color: '#A57C33', fontSize: '14px' }}>Billing Currency</span>
              <span style={{ color: '#E6D7C3', fontWeight: '600', fontSize: '14px' }}>USD ($)</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', borderRadius: '8px', border: '1px solid rgba(165, 124, 51, 0.3)' }}>
              <span style={{ color: '#A57C33', fontSize: '14px' }}>Auto-Renewal</span>
              <span style={{ color: '#2ed573', fontWeight: '600', fontSize: '14px' }}>Enabled</span>
            </div>

            <div style={{ padding: '16px', background: 'rgba(46, 213, 115, 0.05)', borderRadius: '8px', border: '1px solid rgba(46, 213, 115, 0.2)', marginTop: '8px' }}>
              <p style={{ color: '#2ed573', fontSize: '13px', lineHeight: '1.6', margin: 0 }}>
                🔒 All payments are processed securely through Stripe. We never store your complete card details.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PaymentWalletPage;
